export class DomUtils {
  /**
   * @static
   * @method isTouchDevice
   * Determines if the current device is a touch-enabled device.
   *
   * This method checks for the presence of touch capabilities by verifying:
   * - The existence of the `ontouchstart` property in the `window` object.
   * - The `maxTouchPoints` property in the `navigator` object (for modern browsers).
   * - The `msMaxTouchPoints` property in the `navigator` object (for older Microsoft browsers).
   *
   * @returns {boolean} `true` if the device supports touch input, otherwise `false`.
   */
  static isTouchDevice() {
    return (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      navigator.msMaxTouchPoints > 0
    )
  }

  /**
   * @static
   * @method getFocusableElements
   * Retrieves all focusable elements within a specified container.
   *
   * Focusable elements include:
   * - Links (`<a>` elements with an `href` attribute)
   * - Buttons that are not disabled
   * - Textareas
   * - Input fields
   * - Select elements
   * - Elements with a `tabindex` attribute (excluding those with `tabindex="-1"`)
   *
   * Filters out elements that are disabled or have the `aria-hidden` attribute.
   *
   * @param {HTMLElement} container - The container element to search within.
   * @returns {HTMLElement[]} An array of focusable elements within the container.
   */
  static getFocusableElements(container) {
    return Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
      )
    ).filter(el => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden'));
  }

  static _focusTraps = new WeakMap();

  static trapFocus(container) {
    const focusableEls = this.getFocusableElements(container);
    const firstEl = focusableEls[0];
    const lastEl = focusableEls[focusableEls.length - 1];

    if (!firstEl || !lastEl) return;

    const handler = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus({preventScroll: false, focusVisible: false});
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus({preventScroll: false, focusVisible: false});
        }
      }
    };

    container.addEventListener('keydown', handler);

    // container に対するリスナーを記録
    this._focusTraps.set(container, handler);
  }

  static releaseFocus(container) {
    const handler = this._focusTraps.get(container);
    if (handler) {
      container.removeEventListener('keydown', handler);
      this._focusTraps.delete(container);
    }
  }

  static waitForTransitionIfExists(element, expectedProperties = null) {
    if (!(element instanceof Element)) {
      console.warn('waitForTransitionIfExists: 無効な element が渡されました', element);
      return Promise.reject(new Error('Invalid element passed to waitForTransitionIfExists'));
    }

    const computedStyle = getComputedStyle(element);
    const transitionDuration = computedStyle.transitionDuration.split(',').map(d => parseFloat(d) || 0);
    const transitionDelay = computedStyle.transitionDelay.split(',').map(d => parseFloat(d) || 0);
    const totalDurations = transitionDuration.map((d, i) => d + (transitionDelay[i] || 0));
    const hasTransition = totalDurations.some(time => time > 0);

    if (!hasTransition) {
      return Promise.resolve();
    }

    let properties;

    if (expectedProperties === null) {
      const transitionProperty = computedStyle.transitionProperty;
      properties = transitionProperty === 'all'
        ? [] // 全プロパティ対象
        : transitionProperty.split(',').map(p => p.trim());
    } else if (Array.isArray(expectedProperties)) {
      properties = expectedProperties;
    } else {
      console.warn('waitForTransitionIfExists: expectedProperties は null または string[] である必要があります', expectedProperties);
      return Promise.reject(new Error('Invalid expectedProperties type (must be null or string[])'));
    }

    const isTargetingAll = properties.length === 0;

    return new Promise((resolve) => {
      const remaining = new Set(properties);

      const onEnd = (e) => {
        if (isTargetingAll || remaining.has(e.propertyName)) {
          remaining.delete(e.propertyName);

          if (isTargetingAll || remaining.size === 0) {
            element.removeEventListener('transitionend', onEnd);
            resolve();
          }
        }
      };

      element.addEventListener('transitionend', onEnd);
    });
  }
}
